// ─────────────────────────────────────────────────────────────────────────────
// GAME  –  React wrapper around the canvas + game loop
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { startGame, GameHandle } from './engine/gameLoop';
import { MobileControls } from './MobileControls';
import { Link } from 'react-router-dom';
import './Game.css';

/** Height (px) reserved at the bottom for the mobile controls overlay. */
const MOBILE_CONTROLS_HEIGHT = 160;

function useIsTouch(): boolean {
  const [touch, setTouch] = useState(false);
  useEffect(() => {
    const onTouch = () => setTouch(true);
    window.addEventListener('touchstart', onTouch, { once: true });
    if (navigator.maxTouchPoints > 0) setTouch(true);
    return () => window.removeEventListener('touchstart', onTouch);
  }, []);
  return touch;
}

export const Game: React.FC = () => {
  const canvasRef   = useRef<HTMLCanvasElement>(null);
  const handleRef   = useRef<GameHandle | null>(null);
  const isTouch     = useIsTouch();
  const [started, setStarted] = useState(false);
  const [won, setWon]         = useState(false);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width  = window.innerWidth;
    // On touch devices, shrink the canvas so the game world sits above
    // the mobile controls overlay and the character isn't hidden behind it.
    canvas.height = isTouch
      ? Math.max(100, window.innerHeight - MOBILE_CONTROLS_HEIGHT)
      : window.innerHeight;
  }, [isTouch]);

  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  useEffect(() => {
    if (!started) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handle = startGame(canvas, {
      onWin:     () => setWon(true),
      onRestart: () => setWon(false),
    });
    handleRef.current = handle;
    return () => {
      handle.cleanup();
      handleRef.current = null;
    };
  }, [started]);

  const restart = () => {
    handleRef.current?.restart();
    setWon(false);
  };

  return (
    <div className="game-root">
      <canvas ref={canvasRef} className="game-canvas" />

      {!started && (
        <div className="game-splash">
          <div className="splash-inner">
            <p className="splash-sub">{'<'} GEORGE FREEDLAND {'/>'}  </p>
            <h1 className="splash-title">PORTFOLIO.EXE</h1>
            <p className="splash-tagline">A 2D platformer biography</p>
            <div className="splash-divider" />
            <ul className="splash-controls">
              <li><span>MOVE</span> A D / ← →</li>
              <li><span>JUMP</span> W / ↑ / Space</li>
              <li><span>ACTION</span> E · talk & jetpack thrust</li>
              <li><span>TIP</span> talk to the old astronaut first</li>
            </ul>
            <button
              className="splash-start"
              onClick={() => setStarted(true)}
            >
              [ PRESS START ]
            </button>
            <Link to="/classic" className="splash-classic">
              prefer plain text? → classic view
            </Link>
          </div>
        </div>
      )}

      {started && isTouch && <MobileControls />}

      {started && (
        <Link to="/classic" className="game-classic-link" title="Classic view">
          [ classic ]
        </Link>
      )}

      {won && (
        <div className="win-overlay" role="dialog" aria-labelledby="win-title">
          <div className="win-inner">
            <h2 id="win-title" className="win-title">YOU WIN.</h2>
            <p className="win-role">George Freedland · Software Engineer</p>
            <a className="win-email" href="mailto:george.freedland@gmail.com">
              george.freedland@gmail.com
            </a>
            <div className="win-links">
              <a
                href="https://github.com/George-Freedland"
                target="_blank"
                rel="noopener noreferrer"
              >
                [ GitHub ]
              </a>
              <a
                href="https://www.linkedin.com/in/george-freedland"
                target="_blank"
                rel="noopener noreferrer"
              >
                [ LinkedIn ]
              </a>
              <a href="mailto:george.freedland@gmail.com">
                [ Email ]
              </a>
            </div>
            <p className="win-actions">
              <button type="button" className="win-action-btn" onClick={restart}>
                press R
              </button>
              {' to play again   |   '}
              <Link to="/classic" className="win-action-link">
                /classic
              </Link>
              {' for plain site'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
